import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Segmented,
  Skeleton,
  Spin,
  Alert,
  Tooltip,
  Button,
  Pagination,
} from "antd";
import { Header } from "antd/es/layout/layout";
import {
  CalendarOutlined,
  CrownOutlined,
  FileDoneOutlined,
  LockOutlined,
  LeftOutlined,
  RightOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { UiTextsContext, LangContext } from "../../contexts/Contexts";
import { useUser } from "../../../infrastructure/context/UserContext";
import ResponsiveNavigation from "../../components/NewSideBar/ResponsiveNavigation";
import NewNavBar from "../newHome/NewNavBar/NewNavBar";
import {
  GUIDE_VIEWER_ORIGIN,
  buildGuide2ViewerPath,
} from "../../routes/guides2Origins";
import {
  GUIDES2_FILTERS,
  useGuides2Catalog,
} from "../../../infrastructure/hooks/useGuides2Catalog";
import {
  GUIDE_3DS_OUTCOME,
  useGuide3dsReturn,
} from "../../../infrastructure/hooks/useGuide3dsReturn";
import { GUIDE_VARIANTS } from "../../../shared/constants/guideVariants";
import { formatAmount } from "../../../shared/utils/currency";
import GuideBetaBadge from "./components/GuideBetaBadge";
import GuideBetaDisclaimer from "./components/GuideBetaDisclaimer";
import GuidePurchasePanel from "./components/GuidePurchasePanel";
import GuideActivationOverlay from "./components/GuideActivationOverlay";
import { createGuidesText } from "../../../shared/utils/translations/guides2Texts";
import { resolveGuideCategoryLabel } from "../../../shared/utils/translations/guideCategories";
import "./Styles/Guides2.css";

// Guides 2.0: a diferencia de guidesSection/ (HTML autocontenido servido como
// asset estático), aquí el HTML vive en BD y llega por API.
//
// Desde VIVA-312 una guía puede tener la variante gratuita, la de pago o las
// dos bajo el mismo guideId: el catálogo devuelve una fila por guía y esta
// pantalla pinta una sola tarjeta, con una acción por modalidad disponible. El
// contenido no se trae aquí: al abrir una modalidad se navega al visor, que es
// quien llama a obtener/contenido-guia con guideId y guideVariantTypeCode.
//
// La tarjeta es la misma pieza visual que la de Eventos (EventListCard): sello
// de estado y precio sobre la portada, título, autor en coral, descripción a
// una línea con tooltip, filas de detalle con icono y un pie con los datos a la
// izquierda y la acción a la derecha. Ver Styles/Guides2.css, que reusa las
// medidas de NewEventList.css.

// Con texto largo la tarjeta recorta a una línea; el tooltip solo se arma
// cuando de verdad hay algo recortado. Mismo criterio que EventListCard.
const shouldShowTooltip = (value = "", minLength = 32) =>
  String(value).trim().length > minLength;

// VIVA-1499 (PARCHE TEMPORAL) — cuanto se deja leer el aviso antes de recargar.
export const RECARGA_TRAS_ESPERA_MS = 2000;

// VIVA-1214 — `purchaseEnabled` es la bandera de cobro.
//
// Apagada (el catalogo de /guides2, que es el valor por omision) la pantalla se
// comporta igual que siempre: una guia de pago sin acceso muestra el candado
// "Requiere compra" y no ofrece nada mas. Encendida —la liga oculta
// /guides2/contratar— esa misma tarjeta deja ver el precio y ofrece comprarla.
//
// Es la MISMA pantalla con una bandera, no una vista duplicada (decision del
// 09/09 en el ticket): asi el precio y el estado Beta no se pueden desincronizar
// entre las dos (CA-04).
// `embedded` es la pestana "Mis guias" del perfil (/user/my-guides): la MISMA
// pantalla, con las guias compradas y sin el lienzo de pagina —encabezado, menu
// lateral, titulo y filtros los pone el perfil—. Va como bandera y no como vista
// aparte por la misma razon que `purchaseEnabled` (decision del 09/09 en
// VIVA-1214): dos copias de la tarjeta se desincronizan, y la tarjeta es donde
// viven el estado de la guia, el sello Beta y el acceso.
const Guides2 = ({ purchaseEnabled = false, embedded = false }) => {
  const { texts } = useContext(UiTextsContext);
  const { lang } = useContext(LangContext);
  const isEnglish = lang === "en";
  const t = createGuidesText(isEnglish);
  const navigate = useNavigate();
  const { idUsuario } = useUser();
  // Que tarjeta tiene el precio desplegado. Uno solo a la vez: el precio se
  // pide, no se impone sobre todo el catalogo.
  // El cupon y el intento de pago viven dentro de `GuidePurchasePanel`: al
  // cambiar de tarjeta el panel se desmonta, asi que un codigo escrito en una
  // guia no aparece en la siguiente.
  const [openPriceGuideId, setOpenPriceGuideId] = useState(null);

  const {
    filter,
    setFilter,
    visibleCards,
    loading,
    error,
    page,
    totalPages,
    totalItems,
    pageSize,
    hasNextPage,
    showPagination,
    emptyByPaidFilter,
    completingCards,
    goToPage,
    goToPreviousPage,
    goToNextPage,
    refreshGuideAccess,
    awaitGuideActivation,
    activatingGuideId,
    activatedGuideId,
    activationExpired,
  } = useGuides2Catalog({
    lang,
    userId: idUsuario,
    onlyPaid: purchaseEnabled,
    initialFilter: embedded
      ? GUIDES2_FILTERS.PURCHASED
      : GUIDES2_FILTERS.ALL,
  });

  // VIVA-1214 — quien vuelve del 3DS de su banco aterriza aqui con una carga
  // completa de la pagina y sin panel abierto, asi que el resultado del pago no
  // lo puede atender `GuidePurchasePanel`: se resuelve a nivel de pantalla. Solo
  // en la liga de contratacion, que es el unico `return_url` que existe.
  //
  // Al volver se refresca el acceso de la guia que se estaba comprando. Si el
  // retorno no trajo cual era —puede pasar—, `refreshGuideAccess` recarga igual
  // las compras, que es lo que se hacia antes de esto.
  const atenderRetorno3ds = useCallback(
    ({ guideId } = {}) => refreshGuideAccess?.(guideId),
    [refreshGuideAccess]
  );

  const { checking: confirmando3ds, outcome: resultado3ds } = useGuide3dsReturn({
    enabled: purchaseEnabled,
    lang,
    onGranted: atenderRetorno3ds,
  });

  // El resultado se pinta como banner y no como notificacion flotante: quien
  // regresa del banco lleva varios segundos fuera del sitio y un mensaje que se
  // desvanece solo se le puede pasar.
  const aviso3ds =
    resultado3ds === GUIDE_3DS_OUTCOME.GRANTED
      ? { type: "success", text: t("paymentSuccess") }
      : resultado3ds === GUIDE_3DS_OUTCOME.PENDING
      ? { type: "info", text: t("paymentPendingAccess") }
      : resultado3ds === GUIDE_3DS_OUTCOME.FAILED
      ? { type: "error", text: t("paymentRedirectFailed") }
      : null;

  // VIVA-1214 — al terminar un pago se vuelve a preguntar por el acceso de ESA
  // guia, que es lo que hace cambiar su tarjeta a "Leer version completa".
  //
  // Si el webhook todavia no ha corrido, la espera sigue en el hook del
  // catalogo y no aqui ni en el panel: el panel se desmonta en cuanto la
  // tarjeta deja de ofrecer la compra o el comprador cierra el precio, y hasta
  // ahora ahi terminaba todo —la guia se habilitaba dos segundos despues y la
  // tarjeta se quedaba en "Ver precio" hasta recargar la pagina a mano.
  const handlePurchased = useCallback(
    async (card, resultado) => {
      await refreshGuideAccess?.(card.guideId);
      if (resultado?.granted) return;

      awaitGuideActivation?.({
        guideId: card.guideId,
        paymentIntentId: resultado?.paymentIntentId,
      });
    },
    [refreshGuideAccess, awaitGuideActivation]
  );

  // VIVA-1499 — PARCHE TEMPORAL, QUITAR CUANDO BACKEND LO CORRIJA.
  //
  // Si la espera se agota sin que la guia quede habilitada, se recarga la
  // pagina. Una carga limpia vuelve a pedir catalogo, compras y detalle sin
  // nada guardado en memoria, asi que si el webhook llego tarde —despues de que
  // esta pantalla dejo de preguntar— la compra se ve sin que nadie tenga que
  // recargar a mano.
  //
  // Lo que este parche NO puede hacer, y conviene no esperarlo: si el webhook
  // no corrio en absoluto, la recarga no inventa el acceso. Comprobado el
  // 01/10/2026 con un pago real de prueba (guia 68): el acceso nunca se
  // registro y recargar no cambio nada. Por eso esto es un paliativo mientras
  // VIVA-1499 siga abierto, no la solucion.
  //
  // No se repite en bucle: `activationExpired` es estado de React y muere con
  // la recarga, y solo lo enciende un pago terminado en esta misma visita.
  useEffect(() => {
    if (!activationExpired) return undefined;

    const temporizador = setTimeout(
      () => window.location.reload(),
      RECARGA_TRAS_ESPERA_MS
    );

    return () => clearTimeout(temporizador);
  }, [activationExpired]);

  // Las esperas de un pago ya hecho NO van en un banner: tapan la pantalla
  // entera (ver GuideActivationOverlay). Un aviso en una esquina deja el
  // catalogo usable y lo primero que invita a hacer es volver a pulsar
  // "Comprar guia" sobre la guia que se acaba de pagar.
  //
  // El resultado —exito, pendiente, fallo— si es un banner: ahi ya no hay nada
  // corriendo y la persona tiene que poder seguir navegando.
  const esperaBloqueante = confirmando3ds
    ? t("paymentRedirectChecking")
    : activatingGuideId
    ? t("waitingAccess")
    : activationExpired
    ? t("refreshingAfterPayment")
    : "";

  const aviso = activatedGuideId
    ? { type: "success", text: t("paymentSuccess") }
    : aviso3ds;

  // El visor tiene que saber de donde se abrio: desde la liga de contratacion
  // hay que volver ahi, no al catalogo general, y desde la pestana del perfil
  // hay que volver al perfil.
  const viewerOrigin = embedded
    ? GUIDE_VIEWER_ORIGIN.PROFILE_GUIDES
    : purchaseEnabled
    ? GUIDE_VIEWER_ORIGIN.CHECKOUT
    : GUIDE_VIEWER_ORIGIN.CATALOG;

  const openVariant = (card, variantTypeCode) =>
    navigate(buildGuide2ViewerPath(card.guideId, variantTypeCode, viewerOrigin));

  // Al pulsar la tarjeta se abre la mejor modalidad a la que el usuario tenga
  // acceso: la de pago si la compró, y si no la gratuita. Sin acceso a ninguna
  // el clic no hace nada, para no llevar a una pantalla que va a decir que no
  // se puede leer.
  const openBestVariant = (card) => {
    if (card.canReadPaid) return openVariant(card, GUIDE_VARIANTS.PAID);
    if (card.canReadFree) return openVariant(card, GUIDE_VARIANTS.FREE);
    return undefined;
  };

  const filterOptions = [
    { label: t("filterAll"), value: GUIDES2_FILTERS.ALL },
    {
      label: t("filterPurchased"),
      value: GUIDES2_FILTERS.PURCHASED,
    },
  ];

  // Sello de estado sobre la portada, equivalente al de estado del evento:
  // verde cuando la guía se puede leer, gris cuando exige compra.
  const getStatusBadge = (card) => {
    if (card.canReadPaid) {
      return {
        label: t("badgePurchased"),
        className: "g2-status-badge--purchased",
      };
    }

    // Lo que manda es si queda algo por comprar, no si ademas hay una version
    // gratuita. Una guia con las dos modalidades exige compra para la completa y
    // asi lo dice su pie ("Requiere compra"); sellarla como "Gratuita" decia lo
    // contrario en la misma tarjeta. Descartado ya el acceso de pago, tener
    // variante de pago significa que falta comprarla.
    if (card.hasPaidVariant) {
      return {
        label: t("badgePaid"),
        className: "g2-status-badge--paid",
      };
    }

    if (card.hasFreeVariant) {
      return {
        label: t("badgeFree"),
        className: "g2-status-badge--free",
      };
    }

    return {
      label: t("badgePaid"),
      className: "g2-status-badge--paid",
    };
  };

  // Solo se ofrece a la venta una guia de pago a la que todavia no se tiene
  // acceso. Una guia gratuita, o una ya comprada, se lee: la bandera no le
  // cambia nada.
  const canPurchase = (card) =>
    purchaseEnabled && card.hasPaidVariant && !card.canReadPaid;

  const togglePrice = (card) => {
    setOpenPriceGuideId((previous) =>
      previous === card.guideId ? null : card.guideId
    );
  };

  const renderCover = (card) => {
    const statusBadge = getStatusBadge(card);
    const priceLabel = card.hasPaidVariant
      ? formatAmount(card.price, card.currencyCode, lang)
      : "";

    return (
      <div className="g2-image-wrap">
        {card.coverImageUrl ? (
          <img
            src={card.coverImageUrl}
            alt={card.name}
            className="g2-image"
            // Una portada rota no debe dejar el icono de imagen fallida:
            // se oculta y queda el fondo degradado de la tarjeta.
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <FileDoneOutlined className="g2-image-icon" />
        )}

        <div className="g2-image-badges">
          <span className={`g2-status-badge ${statusBadge.className}`}>
            {statusBadge.label}
          </span>
          {/* VIVA-1246, superficie 1: el distintivo acompana al sello de estado.
              No aparece hasta que exista el dato y la clave del texto. */}
          <GuideBetaBadge guide={card} texts={texts} isEnglish={isEnglish} />
        </div>

        {priceLabel ? (
          <div className="g2-price-badge">
            <span className="g2-price-amount">{priceLabel}</span>
          </div>
        ) : null}
      </div>
    );
  };

  // Una acción por modalidad disponible: puede haber dos botones en la misma
  // tarjeta cuando la guía tiene ambas y el usuario compró la de pago.
  const renderFooterActions = (card) => (
    <div className="g2-footer-actions">
      {!purchaseEnabled && card.canReadFree && (
        <button
          type="button"
          className="g2-btn g2-btn--ghost"
          onClick={(e) => {
            e.stopPropagation();
            openVariant(card, GUIDE_VARIANTS.FREE);
          }}
        >
          <span>{t("readFree")}</span>
          <span className="g2-btn-icon">
            <RightOutlined />
          </span>
        </button>
      )}

      {card.hasPaidVariant &&
        (card.canReadPaid ? (
          <button
            type="button"
            className="g2-btn"
            onClick={(e) => {
              e.stopPropagation();
              openVariant(card, GUIDE_VARIANTS.PAID);
            }}
          >
            <span>{t("readFull")}</span>
            <span className="g2-btn-icon">
              <RightOutlined />
            </span>
          </button>
        ) : canPurchase(card) ? (
          <button
            type="button"
            className="g2-btn"
            aria-expanded={openPriceGuideId === card.guideId}
            onClick={(e) => {
              e.stopPropagation();
              togglePrice(card);
            }}
          >
            <span>
              {openPriceGuideId === card.guideId ? t("hidePrice") : t("seePrice")}
            </span>
            <span className="g2-btn-icon">
              <RightOutlined />
            </span>
          </button>
        ) : (
          <span className="g2-locked">
            <LockOutlined className="g2-locked-icon" />
            {t("purchaseRequired")}
          </span>
        ))}
    </div>
  );

  // VIVA-1214: el panel de compra (desglose del servidor, cupon y pago con
  // Stripe) vive en su propio componente. Al completarse un pago se vuelven a
  // pedir las compras, que es lo que cambia la tarjeta a "Leer version
  // completa" en cuanto el webhook habilita la guia.
  const renderPurchasePanel = (card) => (
    <GuidePurchasePanel
      card={card}
      lang={lang}
      t={t}
      texts={texts}
      isEnglish={isEnglish}
      onPurchased={handlePurchased}
    />
  );

  const renderCardBody = (card) => {
    const titleTooltip = shouldShowTooltip(card.name, 36) ? card.name : "";
    const descriptionTooltip = shouldShowTooltip(card.description, 44)
      ? card.description
      : "";

    return (
      <div className="g2-body">
        <div className="g2-content">
          <Tooltip
            title={titleTooltip}
            overlayClassName="g2-card-tooltip"
            mouseEnterDelay={0.35}
          >
            <h3 className="g2-title">{card.name}</h3>
          </Tooltip>

          {card.authorNickName ? (
            <span className="g2-author">
              {t("authorPrefix")} {card.authorNickName}
            </span>
          ) : null}

          {card.description ? (
            <Tooltip
              title={descriptionTooltip}
              overlayClassName="g2-card-tooltip"
              mouseEnterDelay={0.35}
            >
              <p className="g2-desc">{card.description}</p>
            </Tooltip>
          ) : null}

          {card.categoryName ? (
            <div className="g2-category-row">
              <TagOutlined />
              <span>
                {resolveGuideCategoryLabel(card.categoryName, {
                  texts,
                  isEnglish,
                })}
              </span>
            </div>
          ) : null}

          {card.purchaseDate ? (
            <div className="g2-date-row">
              <CalendarOutlined />
              <span>
                {t("purchasedOn")}{" "}
                {new Date(card.purchaseDate).toLocaleDateString(t("dateLocale"))}
              </span>
            </div>
          ) : null}
        </div>

        {/* VIVA-1165 / VIVA-1248: el disclaimer de Beta va en la ficha, antes
            del boton de compra. Solo donde la guia SE VENDE (liga de
            contratacion): en /guides2 no hay boton de compra y el texto habla
            de "al comprarla". El componente decide si la guia es Beta. */}
        {canPurchase(card) ? (
          <GuideBetaDisclaimer guide={card} texts={texts} isEnglish={isEnglish} />
        ) : null}

        <div className="g2-footer">
          {/* En la liga de contratacion solo se vende la modalidad de pago —la
              gratuita ni se ofrece leer ahi—, asi que el pie dice "De pago" y
              no lista "Gratuita". En /guides2 sigue diciendo que versiones
              existen. */}
          <div className="g2-footer-meta">
            {purchaseEnabled ? (
              card.hasPaidVariant && (
                <span className="g2-meta-item">
                  <CrownOutlined />
                  {t("variantPaid")}
                </span>
              )
            ) : (
              <>
                {card.hasFreeVariant && (
                  <span className="g2-meta-item">
                    <FileDoneOutlined />
                    {t("variantFree")}
                  </span>
                )}
                {card.hasPaidVariant && (
                  <span className="g2-meta-item">
                    <CrownOutlined />
                    {t("variantFull")}
                  </span>
                )}
              </>
            )}
          </div>

          {renderFooterActions(card)}
        </div>
      </div>
    );
  };

  const renderCards = () => {
    if (loading) {
      return (
        <Row gutter={[24, 24]} className="guides2-list-container">
          {[0, 1, 2].map((key) => (
            <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8} key={key}>
              <Card
                className="g2-card g2-card-locked"
                cover={<div className="g2-image-wrap g2-image-wrap--skeleton" />}
              >
                <Skeleton active paragraph={{ rows: 3 }} />
              </Card>
            </Col>
          ))}
        </Row>
      );
    }

    // El catálogo dejó de ser anónimo: sin sesión el backend responde 401 y lo
    // honesto es pedir que inicie sesión, no decir que algo falló.
    if (error === "auth") {
      return (
        <Alert
          type="info"
          showIcon
          message={t("errorAuth")}
        />
      );
    }

    if (error) {
      return (
        <Alert
          type="error"
          showIcon
          message={t("errorCatalog")}
        />
      );
    }

    // Vacío con la misma tarjeta que el listado de eventos (.el-empty-state-card):
    // icono, título y explicación centrados dentro del mismo lienzo blanco.
    if (visibleCards.length === 0) {
      return (
        <div className="g2-empty-state-card">
          <FileDoneOutlined className="g2-empty-icon" />
          <h3 className="g2-empty-title">
            {emptyByPaidFilter
              ? t("emptySaleTitle")
              : filter === GUIDES2_FILTERS.PURCHASED
              ? t("emptyPurchasedTitle")
              : t("emptyCatalogTitle")}
          </h3>
          <p className="g2-empty-description">
            {emptyByPaidFilter
              ? t("emptySaleText")
              : filter === GUIDES2_FILTERS.PURCHASED
              ? t("emptyPurchasedText")
              : t("emptyCatalogText")}
          </p>
        </div>
      );
    }

    // Con un panel de compra abierto, la fila deja de estirar sus columnas: si
    // no, la tarjeta que crece arrastra a sus vecinas a su misma altura y toda
    // la fila se "abre". Las tarjetas cerradas ya miden lo mismo por si solas
    // (comprobado: 323 px las siete con y sin estirar), asi que la fila sigue
    // pareja. La clase va escrita completa para que PurgeCSS la conserve.
    const panelAbierto = visibleCards.some(
      (card) => card.guideId === openPriceGuideId && canPurchase(card)
    );

    return (
      <Row
        gutter={[24, 24]}
        className={
          panelAbierto
            ? "guides2-list-container guides2-list-container--panel-open"
            : "guides2-list-container"
        }
      >
        {visibleCards.map((card) => {
          const abrible = card.canReadFree || card.canReadPaid;
          const comprable = canPurchase(card);
          const accionable = abrible || comprable;

          return (
            <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8} key={card.guideId}>
              <Card
                hoverable={accionable}
                className={`g2-card ${accionable ? "" : "g2-card-locked"}`}
                // Pulsar la tarjeta hace lo mismo que su boton: donde se
                // vende, abre el precio; donde se lee, abre la guia.
                onClick={() =>
                  comprable ? togglePrice(card) : abrible && openBestVariant(card)
                }
                cover={renderCover(card)}
              >
                {renderCardBody(card)}
                {comprable && openPriceGuideId === card.guideId
                  ? renderPurchasePanel(card)
                  : null}
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  // Mientras se cargan los datos se dice en pantalla, en vez de dejar al usuario
  // delante de una rejilla a medio hacer preguntandose si eso es todo.
  //
  // Cubre las DOS esperas, no solo la primera: el catalogo responde pronto, pero
  // el nombre traducido y el acceso por variante los trae `detalle-guia`, que
  // llega despues —hasta entonces la tarjeta muestra el `code` de la guia y
  // todavia no sabe si ofrecer "Ver precio" o "Leer version completa"—. Con el
  // esqueleto debajo mientras llega el catalogo y las tarjetas atenuadas
  // mientras se completan, el aviso es el mismo en las dos fases.
  const cargandoDatos = loading || completingCards === true;

  const renderList = () => (
    <Spin spinning={cargandoDatos} size="large" tip={t("loadingGuides")}>
      <section className="guides2-list">{renderCards()}</section>
    </Spin>
  );

  // Las flechas del paginado numerado llevan palabra, como en el listado de
  // eventos; los numeros los pinta Ant Design.
  const renderPaginationItem = (numero, type, originalElement) => {
    if (type === "prev") {
      return <button className="g2-pagination-nav">{t("previous")}</button>;
    }

    if (type === "next") {
      return <button className="g2-pagination-nav">{t("next")}</button>;
    }

    return originalElement;
  };

  // Dos paginados, porque las dos listas saben cosas distintas:
  //
  //  - "Mis guias compradas" llega completa en una sola llamada, asi que se
  //    conoce el total y se numeran las paginas, igual que el listado de
  //    eventos. Es el mismo control en las dos superficies donde aparece —la
  //    pestana del perfil y el filtro del catalogo—, porque es el mismo hook el
  //    que lo decide.
  //  - El catalogo lo pagina el servidor de 12 en 12 y no devuelve el total, asi
  //    que ahi no hay numero que pintar sin inventarlo: sigue con "Anterior /
  //    Siguiente".
  const renderPagination = () => {
    if (error || !showPagination) return null;

    if (totalPages) {
      return (
        <div className="g2-pagination-wrap">
          <div className="g2-pagination-numbered">
            <Pagination
              current={page}
              total={totalItems}
              pageSize={pageSize}
              onChange={goToPage}
              showSizeChanger={false}
              responsive
              itemRender={renderPaginationItem}
            />
          </div>
          <span className="g2-pagination-info">
            {t("page")} {page} {t("pageOf")} {totalPages}
          </span>
        </div>
      );
    }

    if (visibleCards.length === 0 && page === 1 && !hasNextPage) return null;

    return (
      <div className="g2-pagination-wrap">
        <nav className="g2-pagination" aria-label={t("paginationLabel")}>
          <Button
            icon={<LeftOutlined />}
            disabled={page === 1 || loading}
            onClick={goToPreviousPage}
          >
            {t("previous")}
          </Button>

          <Button disabled={!hasNextPage || loading} onClick={goToNextPage}>
            {t("next")}
            <RightOutlined />
          </Button>
        </nav>

        {/* El catálogo no devuelve total de páginas, así que aquí solo va
            la actual; el sitio donde eventos pone "Página X de Y". */}
        <span className="g2-pagination-info">
          {t("page")} {page}
        </span>
      </div>
    );
  };

  // Dentro del perfil solo va la rejilla y su paginado: el encabezado y el menu
  // lateral ya estan puestos por la pagina que la contiene, y la barra de titulo
  // y filtros sobra —la pestana ya dice "Mis guias" y ofrecer ahi "Todas las
  // guias" se contradiria con su nombre—. El aviso del 3DS tampoco aparece: el
  // retorno del banco solo ocurre en la liga de contratacion.
  if (embedded) {
    return (
      <div className="guides2-embedded">
        {esperaBloqueante ? <GuideActivationOverlay message={esperaBloqueante} /> : null}
        {renderList()}
        {renderPagination()}
      </div>
    );
  }

  return (
    <>
      {esperaBloqueante ? <GuideActivationOverlay message={esperaBloqueante} /> : null}

      <Header className="guides2-page-header">
        <NewNavBar />
      </Header>

      <ResponsiveNavigation
        uiTexts={texts}
        className="guides2-sidebar-navigation"
      />

      <div className="container-guides2">
        <div className="container-guides2-section">
          {/* Misma barra que .events-actions-bar: el título a la izquierda y
              los controles a la derecha, apilados en móvil. */}
          <div className="g2-actions-bar">
            <h1 className="g2-page-title">{t("pageTitle")}</h1>
            <div className="g2-filters">
              <Segmented options={filterOptions} value={filter} onChange={setFilter} />
            </div>
          </div>

          {aviso ? (
            <Alert
              className="g2-3ds-alert"
              type={aviso.type}
              message={aviso.text}
              showIcon
              role="status"
            />
          ) : null}

          {renderList()}

          {renderPagination()}
        </div>
      </div>
    </>
  );
};

export default Guides2;
